import {$KTM, $KTS, KeyMetadatas, KeyTextStrings} from "../../shared/types";
import {DialogFragment, DialogFragmentProps, DialogFragmentState} from "../../shared/PageFragment";
import {Pages, PracticeData} from "../../libwyze-shared/types";
import React, {ReactElement} from "react";
import {StyledBoxColumn, StyledBoxRow, StyledSpan} from "../../shared/StyledComponents";
import {PAGE_FRAGMENT_LG_WIDTH, PD_MD, PD_XLG, PD_XLG2} from "../../shared/dimens";
import {Button, CircularProgress, Grid, TextField, Typography} from "@mui/material";
import {colorPastelBlue} from "../../shared/colors";
import {FormGenContainer} from "../../shared/FormGenContainer";
import {HeroSectionData, TEMPLATES} from "../../vantage-shared/page-components/types";
import defaultTemplate from "./res/images/default_template.png";
import {Site, SiteData} from "../../vantage-shared/types";

enum CreatePracticeSiteStep {
  INPUT_NAME = "input_name",
  IMPORT_DATA = "import_data",
  DATA_PREVIEW = "data_preview",
  SELECT_TEMPLATE = "select_template",
  SITE_PREVIEW = "site_preview",
}

const CREATE_SITE_STEP_KTS = new KeyTextStrings([
  $KTS(CreatePracticeSiteStep.INPUT_NAME, "Input Name"),
  $KTS(CreatePracticeSiteStep.IMPORT_DATA, "Import Data"),
  $KTS(CreatePracticeSiteStep.DATA_PREVIEW, "Data Preview"),
  $KTS(CreatePracticeSiteStep.SELECT_TEMPLATE, "Select Template"),
  // $KTS(CreatePracticeSiteStep.SITE_PREVIEW, "Site Preview"),
]);

enum CreateSiteImportDataSource {
  PROFILE = "profile",
  NONE = "none",
}

type CreateSiteImportDataSourceMetadata = {
  text: string,
  iconUrl: string,
}

const CREATE_SITE_IMPORT_DATA_SOURCE_KTM = new KeyMetadatas<CreateSiteImportDataSourceMetadata>([
  $KTM(CreateSiteImportDataSource.PROFILE, {
    text: "From my Practice's Profile",
    iconUrl: "/icons/site-import-profile.svg"
  }),
  $KTM(CreateSiteImportDataSource.NONE, {text: "Do not import anything", iconUrl: "/icons/site-import-none.svg"}),
]);

export type CreateSiteFragmentProps = DialogFragmentProps & {
  finish: (site: Site) => void,
}

type CreateSiteFragmentState = DialogFragmentState & {
  siteName: string,
  stepIndex: number,
  stepTransitioning?: boolean,
  importDataSource?: CreateSiteImportDataSource,
  siteData?: SiteData,
  templateName?: string,
}

export class CreatePracticeSiteFragment extends DialogFragment<CreateSiteFragmentProps, CreateSiteFragmentState> {

  protected onCreateState(props?: CreateSiteFragmentProps): CreateSiteFragmentState {
    return {
      ...super.onCreateState(props),
      stepIndex: 0,
    };
  }

  private step(): CreatePracticeSiteStep {
    return CREATE_SITE_STEP_KTS.values[this.state.stepIndex].key as CreatePracticeSiteStep;
  }

  private async goNext(): Promise<void> {
    this.setState({
      stepTransitioning: true,
    });
    let extra = {};
    let skipCount = 0;
    switch (this.step()) {
      case CreatePracticeSiteStep.IMPORT_DATA:
        const siteData = new SiteData();
        switch (this.state.importDataSource) {
          default:
          case CreateSiteImportDataSource.NONE:
            skipCount = 1;
            break;
          case CreateSiteImportDataSource.PROFILE: {
            const page = Pages.getInstance().getListItem(PracticeData.provider().practiceId);
            const profile = page.practicePageProfile;
            const basicInfo = profile.basicInfo;
            const providerPages = page.getLinkedProviderPages();
            const address2Line = profile.address.getAddress2Line();
            const practice = siteData.practice;
            practice.name = basicInfo.name;
            practice.logo = basicInfo.logo;
            practice.phoneNumber = basicInfo.phoneNumber || "-";
            practice.email = basicInfo.email || "-";
            extra = {
              siteData: siteData,
            };
          }
            break;
        }
        break;
    }
    this.setState({
      stepIndex: this.state.stepIndex + 1 + skipCount,
      stepTransitioning: false,
      ...extra,
    });
  }

  private async goPrev(): Promise<void> {
    this.setState({
      stepTransitioning: true,
    });
    let skipCount = 0;
    switch (this.step()) {
      case CreatePracticeSiteStep.SELECT_TEMPLATE:
        if (this.state.importDataSource === CreateSiteImportDataSource.NONE) {
          skipCount = 1;
        }
        break;
    }
    this.setState({
      stepIndex: this.state.stepIndex - 1 - skipCount,
      stepTransitioning: false,
    });
  }

  private canNext(): boolean {
    switch (this.step()) {
      default:
        return false;
      case CreatePracticeSiteStep.INPUT_NAME:
        return this.state.siteName?.trim().length >= 3;
      case CreatePracticeSiteStep.IMPORT_DATA:
        return Boolean(this.state.importDataSource);
      case CreatePracticeSiteStep.DATA_PREVIEW:
        return true;
      case CreatePracticeSiteStep.SELECT_TEMPLATE:
        return Boolean(this.state.templateName);
    }
  }

  private canPrev(): boolean {
    switch (this.step()) {
      default:
        return true;
    }
  }

  private onFinish(): void {
    const site = Site.createNew(this.state.siteName, this.state.templateName);
    site.data = this.state.siteData;
    this.props.finish(site);
  }

  renderContent(): React.ReactElement {
    return <StyledBoxColumn style={{width: "84dvw", maxWidth: PAGE_FRAGMENT_LG_WIDTH, minHeight: "84dvh"}}>
      <StyledBoxRow style={{padding: PD_MD}}>
        <Typography variant={"h6"}>Create Site › {CREATE_SITE_STEP_KTS.values[this.state.stepIndex].text}</Typography>
      </StyledBoxRow>
      <StyledBoxColumn
        style={{padding: PD_XLG2, gap: PD_XLG2, flexGrow: 1, overflowY: "hidden", justifyContent: "center"}}>
        {this.renderStep()}
      </StyledBoxColumn>
      <StyledBoxRow style={{padding: PD_MD}}>
        {this.state.stepIndex > 0
          ? <Button
            onClick={() => this.goPrev()}
            disabled={!this.canPrev() || this.state.stepTransitioning}
            variant={"contained"}>
            Previous
          </Button>
          : null}
        <StyledSpan/>
        {this.state.stepIndex < CREATE_SITE_STEP_KTS.values.length - 1
          ? <Button
            onClick={() => this.goNext()}
            disabled={!this.canNext() || this.state.stepTransitioning}
            variant={"contained"}>
            Next
          </Button>
          : null}
        {this.state.stepIndex === CREATE_SITE_STEP_KTS.values.length - 1
          ?
          <Button
            onClick={() => this.onFinish()}
            disabled={!this.state.templateName}
            variant={"contained"}>
            Finish & Create Site
          </Button>
          : null}
      </StyledBoxRow>
    </StyledBoxColumn>
  }

  private renderStep(): ReactElement {
    if (this.state.stepTransitioning) {
      return <div style={{display: "flex", flexGrow: 1, alignItems: "center", justifyContent: "center"}}>
        <CircularProgress/>
      </div>
    }
    switch (this.step()) {
      case CreatePracticeSiteStep.INPUT_NAME:
        return this.renderInputName();
      case CreatePracticeSiteStep.IMPORT_DATA:
        return this.renderImportData();
      case CreatePracticeSiteStep.DATA_PREVIEW:
        return this.renderDataPreview();
      case CreatePracticeSiteStep.SELECT_TEMPLATE:
        return this.renderSelectTemplate();
      case CreatePracticeSiteStep.SITE_PREVIEW:
        return this.renderSitePreview();
    }
  }

  private renderInputName(): ReactElement {
    return <>
      <Typography variant={"h5"} style={{textAlign: "center"}}>Enter a name for your site</Typography>
      <TextField size={"small"} placeholder={"Website name"} value={this.state.siteName} onChange={e => this.setState({siteName: e.target.value})}/>
      <StyledSpan/>
    </>;
  }

  private renderImportData(): ReactElement {
    return <>
      <Typography variant={"h5"} style={{textAlign: "center"}}>How do you want to import your site's data?</Typography>
      <Grid container>
        {CREATE_SITE_IMPORT_DATA_SOURCE_KTM.values.map(value =>
          <Grid xs={12} sm={6}>
            <Button
              onClick={() => this.setState({importDataSource: value.key as CreateSiteImportDataSource})}
              style={{
                width: "100%",
                padding: 0,
                border: this.state.importDataSource === value.key ? "2px solid " + this.theme.palette.primary.main : null
              }}>
              <StyledBoxColumn style={{
                width: "100%",
                padding: PD_XLG,
                aspectRatio: 3 / 2,
                alignItems: "center",
                justifyContent: "center"
              }}>
                <div style={{
                  width: "40%",
                  borderRadius: "100%",
                  aspectRatio: 1,
                  background: colorPastelBlue,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}>
                  <img src={value.metadata.iconUrl} style={{width: "50%", aspectRatio: 1}}/>
                </div>
                <Typography>{value.metadata.text}</Typography>
              </StyledBoxColumn>
            </Button>
          </Grid>
        )}
      </Grid>
    </>;
  }

  private renderDataPreview(): ReactElement {
    return <FormGenContainer content={this.state.siteData}/>;
  }

  private renderSelectTemplate(): ReactElement {
    return <>
      <div style={{width: "100%", overflowY: "scroll"}}>
        <Grid container>
          {TEMPLATES.map(template => <Grid xs={12} sm={6} md={3}>
            <Button
              onClick={() => this.setState({templateName: template.name})}
              style={{
                width: "100%",
                padding: 0,
                border: this.state.templateName === template.name ? "2px solid " + this.theme.palette.primary.main : null
              }}>
              <StyledBoxColumn style={{
                width: "100%",
                padding: PD_XLG,
                aspectRatio: 3 / 2,
                alignItems: "center",
                justifyContent: "center"
              }}>
                <img
                  src={template.thumbnailUrl || defaultTemplate}
                  style={{width: "100%", aspectRatio: 1, objectFit: "cover"}}/>
                <Typography>{template.text}</Typography>
              </StyledBoxColumn>
            </Button>
          </Grid>)}
        </Grid>
      </div>
    </>;
  }

  private renderSitePreview(): ReactElement {
    const template = TEMPLATES[0];
    return <div style={{width: "100%", display: "flex", flexDirection: "column"}}>
      <template.PageSections.HeroSection
        template={template}
        data={HeroSectionData.from({
        title: "Smile Dental Center",
        imageUrl: "/images/background.jpg",
      })}/>
    </div>
  }
}